import { useI18nContext } from '@i18n/i18n-react';
import { User, UserGroupRepresentation } from '@common/api';
import { useUserAndGroupOptions } from './hooks/useUserAndGroupOptions';
import { Text } from '@components/typography/Typography';
import { getFullName, getInitials } from '@data/user/user';
import { getInitial } from '@data/userGroup/userGroup';
import { AvatarBadges, AvatarBadgeProps } from '@components/avatar/AvatarBadges';
import { FlexRow } from '@components/layout/Flex';
import { LinkAlt } from '@components/icons/FarseerIcons';
import { TranslationFunctions } from '@i18n/i18n-types';

interface ShareAvatarsProps {
    userIds: number[];
    userGroupIds: number[];
    publicKey?: boolean;
    placeholder?: string;
    limit?: number;
}

export const ShareAvatars = (props: ShareAvatarsProps) => {
    const { userIds, userGroupIds, publicKey, placeholder, limit } = props;

    const { LL } = useI18nContext();
    const { usersMap, userGroupsMap } = useUserAndGroupOptions();

    const userBadges = userIds
        .filter((id) => usersMap.has(id))
        .map((id) => {
            const user = usersMap.get(id)!;
            return { label: getInitials(user), hoverLabel: getFullName(user), image: user.avatarUrl };
        });
    const userGroupBadges = userGroupIds
        .filter((id) => userGroupsMap.has(id))
        .map((id) => {
            const group = userGroupsMap.get(id)!;
            return { label: getInitial(group), hoverLabel: group.name };
        });

    const data: AvatarBadgeProps[] = [...userBadges, ...userGroupBadges];
    if (publicKey) {
        data.unshift({
            label: 'PK',
            hoverLabel: LL.PUBLIC(),
            icon: <LinkAlt />,
            variant: 'primary',
        });
    }

    const shorten = limit != null && data.length > limit;
    const limited = shorten ? data.slice(0, limit) : data;
    if (shorten) {
        const extra = data.slice(limit);
        limited.push({
            label: `+${data.length - limited.length}`,
            hoverLabel: extra.map((i) => i.hoverLabel).join(', '),
        });
    }

    return (
        <FlexRow $width="100%" style={{ height: '100%' }} $align="center">
            {data.length ? (
                <AvatarBadges data={limited} hasLabel />
            ) : placeholder?.length ? (
                <Text $muted>{placeholder}</Text>
            ) : null}
        </FlexRow>
    );
};

export const formatShareUsersPlainText = (
    userIds: number[] | null,
    userGroupIds: number[] | null,
    usersMap: Map<number, User>,
    userGroupsMap: Map<number, UserGroupRepresentation>,
    LL: TranslationFunctions
) => {
    const out: (string | null)[] = [];

    if (userIds) {
        out.push(
            ...userIds.map((id) => {
                const u = usersMap.get(id) ?? null;
                return u ? u.firstName + ' ' + u.lastName : null;
            })
        );
    }
    if (userGroupIds) {
        out.push(
            ...userGroupIds.map((id) => {
                const ug = userGroupsMap.get(id) ?? null;
                return ug?.name ?? LL.UNKNOWN_GROUP();
            })
        );
    }
    return out.filter((s) => s != null).join(', ');
};
